import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useTenant } from "@/context/tenant-context";
import { notificationsApi } from "@/api/notifications";
import type { NotificationConfig, CreateNotificationConfigInput } from "@/api/notifications";
import { queryKeys } from "@/lib/query-keys";
import { Bell, Plus, Trash2, Power } from "lucide-react";

const CHANNELS = ["teams_webhook", "teams_graph", "outlook"] as const;
const CHANNEL_LABELS: Record<string, string> = {
  teams_webhook: "Teams Webhook",
  teams_graph: "Teams (Graph)",
  outlook: "Outlook",
};

const EVENTS = [
  "task.assigned",
  "task.completed",
  "agent.status_changed",
  "approval.required",
] as const;

export function Notifications() {
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();
  const { data: configs = [], isLoading } = useQuery({
    queryKey: queryKeys.notifications.byCompany(tenantId),
    queryFn: () => notificationsApi.list(tenantId),
  });

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<Partial<CreateNotificationConfigInput>>({
    channel: "teams_webhook",
    webhookUrl: "",
    recipientEmail: "",
    events: [],
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.notifications.byCompany(tenantId) });

  const createMutation = useMutation({
    mutationFn: () =>
      notificationsApi.create({
        companyId: tenantId,
        channel: form.channel as CreateNotificationConfigInput["channel"],
        webhookUrl: form.channel === "teams_webhook" ? form.webhookUrl : undefined,
        recipientEmail: form.channel === "outlook" ? form.recipientEmail : undefined,
        events: form.events!,
      }),
    onSuccess: () => {
      invalidate();
      setShowCreate(false);
      setForm({ channel: "teams_webhook", webhookUrl: "", recipientEmail: "", events: [] });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      notificationsApi.update(id, { enabled }),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => notificationsApi.delete(id),
    onSuccess: invalidate,
  });

  const toggleEvent = (event: string) => {
    const current = form.events ?? [];
    setForm({
      ...form,
      events: current.includes(event)
        ? current.filter((e) => e !== event)
        : [...current, event],
    });
  };

  return (
    <CenterPanel title="Notifications">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-text-secondary">
          {configs.length} channel{configs.length !== 1 ? "s" : ""} configured
        </p>
        <Button size="sm" onClick={() => setShowCreate(!showCreate)}>
          <Plus size={16} className="mr-1.5" />
          Add Channel
        </Button>
      </div>

      {showCreate && (
        <Card className="mb-4">
          <CardContent className="space-y-3 pt-4">
            <select
              value={form.channel}
              onChange={(e) => setForm({ ...form, channel: e.target.value as CreateNotificationConfigInput["channel"] })}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
            >
              {CHANNELS.map((c) => (
                <option key={c} value={c}>{CHANNEL_LABELS[c]}</option>
              ))}
            </select>

            {form.channel === "teams_webhook" && (
              <Input
                placeholder="Webhook URL"
                value={form.webhookUrl}
                onChange={(e) => setForm({ ...form, webhookUrl: e.target.value })}
              />
            )}
            {form.channel === "outlook" && (
              <Input
                placeholder="Recipient email"
                value={form.recipientEmail}
                onChange={(e) => setForm({ ...form, recipientEmail: e.target.value })}
              />
            )}

            <div>
              <p className="mb-2 text-xs font-medium text-text-secondary">Events</p>
              <div className="flex flex-wrap gap-2">
                {EVENTS.map((event) => (
                  <button
                    key={event}
                    onClick={() => toggleEvent(event)}
                    className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                      form.events?.includes(event)
                        ? "border-accent bg-accent/10 text-accent"
                        : "border-border text-text-muted hover:border-text-secondary"
                    }`}
                  >
                    {event}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={!form.events?.length || createMutation.isPending}
                onClick={() => createMutation.mutate()}
              >
                Create
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
              {createMutation.isError && (
                <span className="text-xs text-red-500 self-center">
                  {(createMutation.error as Error).message}
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <p className="text-sm text-text-muted">Loading...</p>
      ) : configs.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Bell size={40} className="mb-3 text-text-muted" />
            <p className="text-sm text-text-secondary">No notification channels configured.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-2">
          {configs.map((cfg: NotificationConfig) => (
            <Card key={cfg.id}>
              <CardContent className="flex items-center justify-between py-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10">
                    <Bell size={20} className="text-accent" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-text-primary">
                      {CHANNEL_LABELS[cfg.channel] ?? cfg.channel}
                    </p>
                    <p className="text-xs text-text-muted">
                      {cfg.events.join(", ")}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={cfg.enabled ? "success" : "default"}>
                    {cfg.enabled ? "active" : "paused"}
                  </Badge>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => toggleMutation.mutate({ id: cfg.id, enabled: !cfg.enabled })}
                  >
                    <Power size={14} />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => deleteMutation.mutate(cfg.id)}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </CenterPanel>
  );
}
